// Admin-only visitor monitor: who's been on the site, by IP. Reads the
// `visitors` collection that the /__visit Cloud Function maintains (one doc
// per IP+UA identity: geo, device, hit count, recent paths, signed-in
// identity). Live via onSnapshot; rules allow signed-in reads only and deny
// all client writes. Each site's Firestore has its own collection, so this
// page shows THIS site's traffic — deploy-wide rollup happens in your head.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebaseConfig';

const LIMIT = 300;

const flag = (cc) =>
  cc && /^[A-Z]{2}$/i.test(cc)
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : '';

function ago(ms) {
  if (!ms) return '—';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

const fmtDate = (ms) =>
  ms ? new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export default function Visitors() {
  const [rows, setRows] = useState(null);
  const [hideBots, setHideBots] = useState(true);
  const [hideSignedIn, setHideSignedIn] = useState(false);

  useEffect(() => {
    const unsub = db
      .collection('visitors')
      .orderBy('lastSeen', 'desc')
      .limit(LIMIT)
      .onSnapshot(
        (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => {
          console.error('[visitors]', err.message);
          setRows([]);
        }
      );
    return unsub;
  }, []);

  const shown = useMemo(
    () =>
      (rows || []).filter(
        (v) => (!hideBots || !v.bot) && (!hideSignedIn || !v.uid)
      ),
    [rows, hideBots, hideSignedIn]
  );
  const dayAgo = Date.now() - 86400000;
  const last24 = shown.filter((v) => v.lastSeen >= dayAgo).length;

  return (
    <div className="cs-page cs-visitors">
      <div className="cs-vis-head">
        <h1>Visitors</h1>
        <div className="cs-vis-stats">
          <span><strong>{shown.length}</strong> visitor{shown.length === 1 ? '' : 's'}</span>
          <span><strong>{last24}</strong> in 24h</span>
        </div>
        <div className="cs-vis-filters">
          <label><input type="checkbox" checked={hideBots} onChange={(e) => setHideBots(e.target.checked)} /> hide bots</label>
          <label><input type="checkbox" checked={hideSignedIn} onChange={(e) => setHideSignedIn(e.target.checked)} /> hide signed-in</label>
        </div>
      </div>
      <p className="cs-help">
        One row per visitor (IP + browser identity), newest activity first. Geo/network from a
        one-time IP lookup. Signed-in visits show the account; “proxy/DC” flags VPN or
        datacenter addresses. This lists {String(process.env.REACT_APP_SITE || 'this site')}&apos;s
        own traffic — each site keeps its own log.
      </p>

      {rows == null && <div className="cs-help">Loading…</div>}
      {rows != null && shown.length === 0 && <div className="cs-help">No visitors recorded yet.</div>}

      <div className="cs-vis-list">
        {shown.map((v) => (
          <div key={v.id} className={`cs-vis-card ${v.bot ? 'is-bot' : ''}`}>
            <div className="cs-vis-row1">
              <span className="cs-vis-ip">{v.ip}</span>
              <span className="cs-vis-geo">
                {flag(v.geo?.countryCode)} {[v.geo?.city, v.geo?.region, v.geo?.country].filter(Boolean).join(', ') || 'unknown location'}
              </span>
              <span className="cs-vis-when" title={fmtDate(v.lastSeen)}>{ago(v.lastSeen)}</span>
            </div>
            <div className="cs-vis-row2">
              <span>{v.device ? `${v.device.browser} · ${v.device.os}${v.device.mobile ? ' · mobile' : ''}` : '—'}</span>
              {v.screen && <span>{v.screen}</span>}
              {v.lang && <span>{v.lang}</span>}
              <span title="visits (page loads)">{v.hits || 1}×</span>
              <span title={`first seen ${fmtDate(v.firstSeen)}`}>since {fmtDate(v.firstSeen)}</span>
            </div>
            <div className="cs-vis-row2 cs-vis-net">
              {(v.geo?.org || v.geo?.isp) && <span>{v.geo.org || v.geo.isp}</span>}
              {v.geo?.proxy && <span className="cs-vis-badge is-warn">proxy/VPN</span>}
              {v.geo?.hosting && <span className="cs-vis-badge is-warn">DC</span>}
              {v.bot && <span className="cs-vis-badge">bot</span>}
              {v.uid && <span className="cs-vis-badge is-user" title={v.uid}>{v.email || 'signed in'}</span>}
              {v.referrer && <span className="cs-vis-ref" title={v.referrer}>via {v.referrer.replace(/^https?:\/\//, '').slice(0, 60)}</span>}
            </div>
            {v.recent?.length > 0 && (
              <div className="cs-vis-paths">
                {[...v.recent].reverse().slice(0, 10).map((r, i) => (
                  <span key={i} className="cs-vis-path" title={fmtDate(r.t)}>{r.p}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
